/**
 * Module registry bootstrap
 *
 * Built-in modules are statically imported. Plugin modules (e.g.
 * companies) are loaded at startup via the runtime plugin loader,
 * which fetches their UI bundle from
 * `/api/plugins/<id>/ui/<entry>` and dynamic-imports it. `main.tsx`
 * awaits `loadAppModules()` before rendering, so by the time React
 * mounts the registry is fully populated.
 */

import type { ModuleDefinition, ModuleRegistry } from "../runtime/contracts";
import { loadPluginModule } from "../runtime/plugins";
import { AgentModule } from "./episodes";
import { SettingsModule } from "./settings";
import { ExtensionsModule } from "./extensions";

const BUILTIN_MODULES: readonly ModuleDefinition[] = [
  AgentModule,
  ExtensionsModule,
  SettingsModule,
];

interface CompaniesPluginUi {
  readonly CompaniesModule: ModuleDefinition;
}

interface ContactsPluginUi {
  readonly ContactsModule: ModuleDefinition;
}

interface ProjectsPluginUi {
  readonly ProjectsModule: ModuleDefinition;
}

interface TelegramPluginUi {
  readonly TelegramModule: ModuleDefinition;
}

interface NotesPluginUi {
  readonly NotesModule: ModuleDefinition;
}

interface EmailPluginUi {
  readonly EmailsModule: ModuleDefinition;
}

interface TriggersPluginUi {
  readonly TriggersModule: ModuleDefinition;
}

interface FilePluginUi {
  readonly FilesModule: ModuleDefinition;
}

interface MeetingsPluginUi {
  readonly MeetingsModule: ModuleDefinition;
}

interface XPluginUi {
  readonly XModule: ModuleDefinition;
}

interface LinkedinPluginUi {
  readonly LinkedinModule: ModuleDefinition;
}

let cachedModules: readonly ModuleDefinition[] | null = null;

/// Fault-isolation (DEC-16): load one plugin's module, returning `null` (and
/// logging) instead of throwing if it fails. This keeps a single broken plugin
/// — especially a future downloaded third-party one — from rejecting
/// `loadAppModules` and blanking the entire app; its tab is simply absent while
/// the rest of the app (and the native Extensions panel) renders.
export async function safeLoad<T>(
  load: Promise<T>,
  pick: (mod: T) => ModuleDefinition,
  id: string,
): Promise<ModuleDefinition | null> {
  try {
    return pick(await load);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`[plugins] failed to load "${id}" — its tab is unavailable:`, e);
    return null;
  }
}

export async function loadAppModules(): Promise<readonly ModuleDefinition[]> {
  if (cachedModules) return cachedModules;
  const [companies, contacts, projects, telegram, notes, email, meetings, file, triggers, x, linkedin] =
    await Promise.all([
      safeLoad(loadPluginModule<CompaniesPluginUi>("companies", "index.tsx"), (m) => m.CompaniesModule, "companies"),
      safeLoad(loadPluginModule<ContactsPluginUi>("contacts", "index.tsx"), (m) => m.ContactsModule, "contacts"),
      safeLoad(loadPluginModule<ProjectsPluginUi>("projects", "index.tsx"), (m) => m.ProjectsModule, "projects"),
      safeLoad(loadPluginModule<TelegramPluginUi>("telegram", "index.tsx"), (m) => m.TelegramModule, "telegram"),
      safeLoad(loadPluginModule<NotesPluginUi>("notes", "index.tsx"), (m) => m.NotesModule, "notes"),
      safeLoad(loadPluginModule<EmailPluginUi>("email", "index.tsx"), (m) => m.EmailsModule, "email"),
      safeLoad(loadPluginModule<MeetingsPluginUi>("meetings", "index.tsx"), (m) => m.MeetingsModule, "meetings"),
      safeLoad(loadPluginModule<FilePluginUi>("file", "index.tsx"), (m) => m.FilesModule, "file"),
      safeLoad(loadPluginModule<TriggersPluginUi>("triggers", "index.tsx"), (m) => m.TriggersModule, "triggers"),
      safeLoad(loadPluginModule<XPluginUi>("x", "index.tsx"), (m) => m.XModule, "x"),
      safeLoad(loadPluginModule<LinkedinPluginUi>("linkedin", "index.tsx"), (m) => m.LinkedinModule, "linkedin"),
    ]);
  // Order preserved; failed plugins (null) are dropped — never blank the app.
  cachedModules = [
    AgentModule,
    contacts,
    companies,
    email,
    projects,
    meetings,
    notes,
    telegram,
    x,
    linkedin,
    ExtensionsModule,
    SettingsModule,
    triggers,
    file,
  ].filter((m): m is ModuleDefinition => m !== null);
  return cachedModules;
}

/// Synchronous snapshot of currently-loaded modules. Empty until
/// `loadAppModules()` has resolved. Tests can override by replacing
/// the export via vi.mock.
export const APP_MODULES: readonly ModuleDefinition[] = new Proxy(BUILTIN_MODULES, {
  get(_target, prop, receiver): unknown {
    if (!cachedModules) {
      throw new Error(
        "APP_MODULES accessed before loadAppModules() resolved — " +
          "main.tsx must await it before rendering App",
      );
    }
    return Reflect.get(cachedModules, prop, receiver) as unknown;
  },
});

export const APP_MODULES_BY_ID = new Map<string, ModuleDefinition>();

export function bootstrapModules(registry: ModuleRegistry): void {
  if (!cachedModules) {
    throw new Error("bootstrapModules: loadAppModules() must resolve first");
  }
  for (const mod of cachedModules) {
    registry.register(mod);
    APP_MODULES_BY_ID.set(mod.id, mod);
  }
}
